import React from 'react';
import { TouchableOpacity, View, StyleSheet } from 'react-native';
import { DRNText } from '@traveloka/district-ui';
import PropTypes from 'prop-types';
import dateFns from 'date-fns';
import idLocale from 'date-fns/locale/id';

import TouchableCard from 'common/components/TouchableCard/TouchableCard';
import R from 'common/res/R';
import BusPnrStatusComponent from '../../shared/components/BusStatusEnum/BusPnrStatusComponent';
import EllipsisIcon from 'common/res/svg/EllipsisIcon';
import { isInSameDayWithPresentDate } from 'common/utils/date/isInSameDay';
import parseSpecificDate from 'common/utils/date/parseSpecificDate';
import { getTimeZoneText } from 'modules/bus/shared/utils/getTimeZoneText';

const styles = StyleSheet.create({
  dateText: {
    ...R.typography.weightMedium,
    fontSize: R.typography.fontSize.medium,
    lineHeight: R.typography.fontSize.medium,
    marginTop: R.spacing.s,
    marginBottom: R.spacing.s,
  },
  card: {
    padding: R.spacing.m,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  firstLineContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 0,
    marginHorizontal: 0,
    marginBottom: R.spacing.xs,
    flex: 1,
  },
  actionButton: {
    position: 'absolute',
    right: 0,
    bottom: R.spacing.xxs,
    paddingHorizontal: R.spacing.xs,
    paddingVertical: R.spacing.xxs,
    backgroundColor: R.color.uiLightStain,
  },
  busPassengerName: {
    ...R.typography.weightBold,
    fontSize: R.typography.fontSize.small,
    lineHeight: R.typography.fontSize.small,
    marginTop: 0,
    marginBottom: 0,
    marginRight: R.spacing.xs,
  },
  busTripCode: {
    fontSize: R.typography.fontSize.micro,
    lineHeight: R.typography.fontSize.micro,
    marginTop: 0,
    marginBottom: 0,
  },
  tripDetailWrapper: {
    margin: 0,
    flex: 1,
    marginBottom: R.spacing.xs,
  },
  tripDetailInfo: {
    fontSize: R.typography.fontSize.tiny,
    lineHeight: R.typography.fontSize.tiny,
    color: R.color.textSecondary,
    marginTop: 0,
    marginBottom: 0,
  },
  specificDateWrapper: {
    margin: 0,
    flex: 1,
    marginBottom: R.spacing.xs,
  },
  specificDate: {
    fontSize: R.typography.fontSize.tiny,
    lineHeight: R.typography.fontSize.tiny,
    marginTop: 0,
    marginBottom: 0,
    color: R.color.textSecondary,
  },
});

const navigateToDetail = props => e => {
  const {
    originLabel,
    destinationLabel,
    pnrCode,
    tripItineraryId,
    isReservationList,
  } = props;
  props.navigation.push('BusTransactionDetail', {
    originLabel,
    destinationLabel,
    pnrCode,
    tripItineraryId,
    isReservationList: isReservationList || undefined,
  });
};

const BusTransactionListItem = props => {
  const {
    busTripCode,
    destinationLabel,
    originLabel,
    busPassenger,
    pnrCode,
    pnrStatus,
    specificDate,
    shouldShowDateBeforeCard,
    isReservationList,
    departureDateTime,
  } = props;
  const timeZoneId = departureDateTime.timeZoneId;
  const departureDate = parseSpecificDate(departureDateTime.specificDate);
  return (
    <View>
      {shouldShowDateBeforeCard && (
        <DRNText style={styles.dateText}>
          {isInSameDayWithPresentDate(specificDate)
            ? `${
                isReservationList ? 'Keberangkatan' : 'Dipesan'
              } Hari Ini (${dateFns.format(specificDate, 'dddd, DD MMM YYYY', {
                locale: idLocale,
              })})`
            : `${
                isReservationList ? 'Keberangkatan' : 'Dipesan'
              } ${dateFns.format(specificDate, 'dddd, DD MMM YYYY', {
                locale: idLocale,
              })}`}
        </DRNText>
      )}
      <TouchableCard onPress={navigateToDetail(props)}>
        <View style={styles.card}>
          <View style={styles.firstLineContainer}>
            <View style={styles.titleContainer}>
              <DRNText style={styles.busPassengerName}>
                {busPassenger && busPassenger.name}
              </DRNText>
              <DRNText numberOfLines={2} style={styles.busTripCode}>
                {pnrCode}
              </DRNText>
            </View>
            {isReservationList &&
              props.onActionButtonPressed && (
                <View>
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={() => {
                      props.onActionButtonPressed(props);
                    }}
                  >
                    <EllipsisIcon />
                  </TouchableOpacity>
                </View>
              )}
          </View>
          <View style={styles.tripDetailWrapper}>
            <DRNText
              style={styles.tripDetailInfo}
            >{`${originLabel} → ${destinationLabel}${busTripCode &&
              ' • ' + busTripCode}`}</DRNText>
          </View>
          <View style={styles.specificDateWrapper}>
            <DRNText style={styles.specificDate}>
              {`${dateFns.format(departureDate, 'dddd, D MMM YYYY', {
                locale: idLocale,
              })} • ${dateFns.format(departureDate, 'HH:mm')} ${getTimeZoneText(
                timeZoneId
              )}`}
            </DRNText>
          </View>
          <BusPnrStatusComponent status={pnrStatus} />
        </View>
      </TouchableCard>
    </View>
  );
};

BusTransactionListItem.propTypes = {
  tripItineraryId: PropTypes.number.isRequired,
  pnrCode: PropTypes.string.isRequired,
  originLabel: PropTypes.string.isRequired,
  destinationLabel: PropTypes.string.isRequired,
  specificDate: PropTypes.instanceOf(Date).isRequired,
  pnrStatus: PropTypes.string.isRequired,
  busPassenger: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired,
  shouldShowDateBeforeCard: PropTypes.bool,
  isReservationList: PropTypes.bool,
};

BusTransactionListItem.defaultProps = {
  shouldShowDateBeforeCard: true,
  isReservationList: false,
};

export default BusTransactionListItem;
